/**
 * PilotReview Component
 *
 * Shows pilot phase results with validation details for each product.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  FileText,
  RefreshCw,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import {
  getPilotReview,
  getValidationBreakdown,
  retryProduct,
  ProductStatus,
  ValidationResults,
} from '@/services/pipelineService';
import { cleanProductUrl } from '@/utils/url-cleaner';

interface PilotReviewProps {
  runId: string;
}

/**
 * Convert technical failure reasons to human-friendly messages
 */
function getHumanFriendlyMessage(failure: { story: number | string; reason: string }): {
  title: string;
  description: string;
  action?: string;
} {
  const reason = failure.reason.toLowerCase();

  // Story 49 - Fact Accuracy
  if (failure.story === 49 || reason.includes('hallucination') || reason.includes('discrepanc')) {
    return {
      title: 'Factual Errors Detected',
      description: 'The generated content contains information that doesn\'t match the product data.',
      action: 'Review the product feed data and ensure all facts are correct.'
    };
  }

  // Story 51 - Persona/Target Audience
  if (failure.story === 51 || reason.includes('persona')) {
    const scoreMatch = reason.match(/(\d+)%?/);
    const score = scoreMatch ? scoreMatch[1] : 'low';
    return {
      title: 'Content Doesn\'t Match Target Audience',
      description: `The writing style scored ${score}% - it may not resonate with your target customers.`,
      action: 'Check if the site\'s persona settings match your actual customer profile.'
    };
  }

  // Story 52 - Category Coherence
  if (failure.story === 52 || reason.includes('coherence')) {
    return {
      title: 'Inconsistent with Category Style',
      description: 'This product\'s content style differs from other products in the same category.',
      action: 'Review category-level content settings or check if this product is in the right category.'
    };
  }

  // Story 53 - Completeness
  if (failure.story === 53 || reason.includes('completeness') || reason.includes('missing spec')) {
    const specMatch = reason.match(/(\d+) missing/);
    const specCount = specMatch ? specMatch[1] : 'some';
    const specsMatch = reason.match(/- (?:CRITICAL|HIGH): ([^-]+)/g);
    const specNames = specsMatch
      ? specsMatch.map(s => s.replace(/- (?:CRITICAL|HIGH): /, '')).join(', ')
      : '';

    return {
      title: `Missing ${specCount} Product Specification(s)`,
      description: specNames
        ? `Customers expect to see: ${specNames}`
        : 'Important product details are missing that customers typically look for.',
      action: 'Add the missing specs to the product feed, or create domain knowledge entries with this information.'
    };
  }

  // Generic fallback
  return {
    title: 'Validation Issue',
    description: failure.reason,
    action: 'Review the product data and try regenerating.'
  };
}

function ValidationBadge({ decision, score }: { decision?: string; score?: number }) {
  if (!decision) {
    return <Badge variant="outline">N/A</Badge>;
  }

  const isPassed = decision === 'PASS' || decision === 'pass';
  const variant = isPassed ? 'default' : 'destructive';
  const icon = isPassed ? (
    <CheckCircle className="h-3 w-3 mr-1" />
  ) : (
    <XCircle className="h-3 w-3 mr-1" />
  );

  return (
    <Badge variant={variant} className="flex items-center">
      {icon}
      {score !== undefined ? `${score}%` : decision}
    </Badge>
  );
}

interface ValidationDetailsDialogProps {
  product: ProductStatus;
  runId: string;
  onRetry: (productId: string) => void;
  onClose?: () => void;
}

function ValidationDetailsDialog({ product, runId, onRetry, onClose }: ValidationDetailsDialogProps) {
  const [retrying, setRetrying] = useState(false);
  const validation = product.validation || product.validationResults;

  // Check if there are missing specs to research
  const hasMissingSpecs = validation?.story53?.missingSpecs?.length > 0;
  const firstMissingSpec = hasMissingSpecs ? validation.story53.missingSpecs[0]?.name : null;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry(product.productId);
      // Close dialog after successful retry
      onClose?.();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{product.productName}</DialogTitle>
        <DialogDescription>
          Validation results after {product.iterations} iteration(s)
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Overall Result */}
        <div className="flex items-center gap-2">
          <span className="font-medium">Overall:</span>
          {validation?.passed ? (
            <Badge className="bg-green-500">PASSED</Badge>
          ) : (
            <Badge variant="destructive">FAILED</Badge>
          )}
        </div>

        {/* Validation Checks - Human Friendly Names */}
        <div className="grid gap-3">
          {/* Fact Accuracy (Story 49) */}
          <div className="p-3 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">✓ Fact Accuracy</span>
              <ValidationBadge decision={validation?.story49?.decision} />
            </div>
            <p className="text-sm text-muted-foreground">
              {validation?.story49?.decision === 'PASS'
                ? 'All product facts are accurate and match the source data'
                : validation?.story49?.discrepancies !== undefined
                  ? `Found ${validation.story49.discrepancies} factual error(s) that need correction`
                  : 'Checking if generated content matches product data...'}
            </p>
          </div>

          {/* Target Audience Match (Story 51) */}
          <div className="p-3 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">👤 Target Audience Match</span>
              <ValidationBadge
                decision={validation?.story51?.decision}
                score={validation?.story51?.score}
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {validation?.story51?.decision === 'PASS'
                  ? 'Content tone and style match the target customer profile'
                  : validation?.story51?.score !== undefined
                    ? `Content only ${validation.story51.score}% aligned with target audience`
                    : 'Checking if content speaks to the right customer...'}
              </p>
              {validation?.story51?.mismatchReason && (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  ⚠️ {validation.story51.mismatchReason}
                </p>
              )}
              {validation?.story51?.recommendations?.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300">How to fix:</p>
                  <ul className="text-xs text-blue-600 dark:text-blue-400 list-disc list-inside mt-1">
                    {validation.story51.recommendations.map((rec: string, i: number) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Category Consistency (Story 52) */}
          <div className="p-3 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">📂 Category Consistency</span>
              <ValidationBadge
                decision={validation?.story52?.decision}
                score={validation?.story52?.score}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {validation?.story52?.decision === 'PASS'
                ? 'Content style is consistent with other products in this category'
                : validation?.story52?.score !== undefined
                  ? `Only ${validation.story52.score}% consistent with category standards`
                  : 'Checking category consistency...'}
            </p>
          </div>

          {/* Specification Coverage (Story 53) */}
          <div className="p-3 border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">📋 Specification Coverage</span>
              <ValidationBadge
                decision={validation?.story53?.decision}
                score={validation?.story53?.score}
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {validation?.story53?.decision === 'PASS'
                  ? 'All required product specifications are included'
                  : validation?.story53?.score !== undefined
                    ? `Only ${validation.story53.score}% of required specs are covered`
                    : 'Checking if all important specs are mentioned...'}
              </p>
              {validation?.story53?.missingSpecs?.length > 0 && (
                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950 rounded">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    Missing specifications that customers need:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {validation.story53.missingSpecs.map((spec: { name: string; severity: string }, i: number) => (
                      <li key={i} className="text-xs flex items-center gap-2">
                        <Badge
                          variant={spec.severity === 'CRITICAL' ? 'destructive' : 'outline'}
                          className="text-xs px-1 py-0"
                        >
                          {spec.severity === 'CRITICAL' ? 'Required' : spec.severity === 'HIGH' ? 'Important' : 'Optional'}
                        </Badge>
                        <span className="text-amber-700 dark:text-amber-300">{spec.name}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    💡 Add these specs to the product feed or create domain knowledge entries
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* What Needs Attention */}
        {validation?.failures && validation.failures.length > 0 && (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <h4 className="font-medium mb-3 text-red-700 dark:text-red-300">
              ⚠️ What Needs Your Attention
            </h4>
            <ul className="space-y-3">
              {validation.failures.map((failure: { story: number | string; reason: string }, index: number) => {
                // Convert technical reasons to human-friendly messages
                const humanMessage = getHumanFriendlyMessage(failure);
                return (
                  <li key={index} className="text-sm">
                    <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
                      <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="font-medium">{humanMessage.title}</span>
                    </div>
                    <p className="ml-6 text-red-500 dark:text-red-400 text-xs mt-1">
                      {humanMessage.description}
                    </p>
                    {humanMessage.action && (
                      <p className="ml-6 text-blue-600 dark:text-blue-400 text-xs mt-1">
                        💡 {humanMessage.action}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Research Created */}
        {product.researchCreated && product.researchCreated.length > 0 && (
          <div>
            <h4 className="font-medium mb-2">Research Created</h4>
            <ul className="space-y-1">
              {product.researchCreated.map((research, index) => (
                <li key={index} className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>{research.title}</span>
                  <Badge variant="outline" className="text-xs">
                    {research.type}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Error */}
        {product.lastError && (
          <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <h4 className="font-medium text-red-700 dark:text-red-300 mb-1">Error</h4>
            <p className="text-sm text-red-600 dark:text-red-400">{product.lastError}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <DialogFooter className="flex gap-2 sm:gap-2">
        {/* View AEO Content button - only show if there's a generationId */}
        {product.generationId && (
          <Link
            to={`/content-review?type=product_aeo&id=${product.generationId}`}
          >
            <Button variant="outline" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              View AEO Content
            </Button>
          </Link>
        )}

        {/* Research Specs button - only show if there are missing specs */}
        {hasMissingSpecs && (
          <Link
            to={`/research/enrichment-review?mode=research&productId=${product.productId}&runId=${runId}${firstMissingSpec ? `&spec=${firstMissingSpec}` : ''}`}
          >
            <Button variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Research Missing Specs
            </Button>
          </Link>
        )}

        {/* Retry button */}
        <Button
          onClick={handleRetry}
          disabled={retrying}
          className="gap-2"
        >
          {retrying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Retry This Product
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/**
 * Wrapper component with controlled Dialog state
 */
function ProductValidationDialog({
  product,
  runId,
  onRetry
}: {
  product: ProductStatus;
  runId: string;
  onRetry: (productId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Eye className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <ValidationDetailsDialog
        product={product}
        runId={runId}
        onRetry={onRetry}
        onClose={() => setOpen(false)}
      />
    </Dialog>
  );
}

export function PilotReview({ runId }: PilotReviewProps) {
  const [products, setProducts] = useState<ProductStatus[]>([]);
  const [summary, setSummary] = useState<{ passed: number; failed: number; total: number } | null>(
    null
  );
  const [breakdown, setBreakdown] = useState<{
    story49: { passed: number; failed: number };
    story51: { passed: number; failed: number; avgScore: number };
    story52: { passed: number; failed: number; avgScore: number };
    story53: { passed: number; failed: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reviewData, breakdownData] = await Promise.all([
        getPilotReview(runId),
        getValidationBreakdown(runId),
      ]);
      setProducts(reviewData.products);
      setSummary(reviewData.summary);
      setBreakdown(breakdownData);
    } catch (error) {
      console.error('Failed to load pilot review data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [runId]);

  const handleRetryProduct = async (productId: string) => {
    try {
      // ⛔ NAME THE PHASE (codex-reviewer-2 @8c4210b5bd3, blocking). The backend now REFUSES an
      // ambiguous multi-phase retry rather than resetting an arbitrary row, so a bare two-arg
      // call returns HTTP 400 and the button silently stops working. This screen is the PILOT
      // review, so the phase is known statically — there is nothing to ask the operator.
      await retryProduct(runId, productId, 'pilot');
      // Reload data to see updated status
      await loadData();
    } catch (error) {
      console.error('Failed to retry product:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{summary?.total || 0}</div>
            <p className="text-sm text-muted-foreground">Total Products</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{summary?.passed || 0}</div>
            <p className="text-sm text-muted-foreground">Passed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{summary?.failed || 0}</div>
            <p className="text-sm text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {summary?.total
                ? Math.round((summary.passed / summary.total) * 100)
                : 0}
              %
            </div>
            <p className="text-sm text-muted-foreground">Success Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Quality Check Summary */}
      {breakdown && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Quality Check Summary</CardTitle>
            <CardDescription>How products performed across each quality check</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="text-sm font-medium">✓ Fact Accuracy</div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{breakdown.story49.passed} ✓</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-red-600">{breakdown.story49.failed} ✗</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium">👤 Audience Match</div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{breakdown.story51.passed} ✓</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-red-600">{breakdown.story51.failed} ✗</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Avg score: {breakdown.story51.avgScore}%
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium">📂 Category Style</div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{breakdown.story52.passed} ✓</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-red-600">{breakdown.story52.failed} ✗</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Avg score: {breakdown.story52.avgScore}%
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium">📋 Spec Coverage</div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">{breakdown.story53.passed} ✓</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-red-600">{breakdown.story53.failed} ✗</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pilot Products</CardTitle>
          <CardDescription>Review individual product results - click the eye icon for details</CardDescription>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No products in pilot phase
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tries</TableHead>
                  <TableHead title="Fact Accuracy">Facts</TableHead>
                  <TableHead title="Target Audience Match">Audience</TableHead>
                  <TableHead title="Category Consistency">Category</TableHead>
                  <TableHead title="Specification Coverage">Specs</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.productId}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {product.productUrl ? (
                        <a
                          href={cleanProductUrl(product.productUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline flex items-center gap-1 text-primary"
                        >
                          {product.productName}
                          <ExternalLink className="h-3 w-3 opacity-50 flex-shrink-0" />
                        </a>
                      ) : (
                        product.productName
                      )}
                    </TableCell>
                    <TableCell>
                      {product.state === 'passed' ? (
                        <Badge className="bg-green-500">Passed</Badge>
                      ) : product.state === 'failed' ? (
                        <Badge variant="destructive">Failed</Badge>
                      ) : product.state === 'loop_detected' ? (
                        <Badge className="bg-orange-500">Looped</Badge>
                      ) : (
                        <Badge variant="outline">{product.state}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{product.iterations}</TableCell>
                    <TableCell>
                      <ValidationBadge
                        decision={product.validation?.story49?.decision}
                      />
                    </TableCell>
                    <TableCell>
                      <ValidationBadge
                        decision={product.validation?.story51?.decision}
                        score={product.validation?.story51?.score}
                      />
                    </TableCell>
                    <TableCell>
                      <ValidationBadge
                        decision={product.validation?.story52?.decision}
                        score={product.validation?.story52?.score}
                      />
                    </TableCell>
                    <TableCell>
                      <ValidationBadge
                        decision={product.validation?.story53?.decision}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <ProductValidationDialog
                          product={product}
                          runId={runId}
                          onRetry={handleRetryProduct}
                        />
                        {product.generationId && (
                          <Link to={`/content-review?type=product_aeo&id=${product.generationId}`}>
                            <Button variant="ghost" size="sm" title="View AEO Content">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
